// pages/wallet/detail.tsx

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBackButton from '@/component/common/top-back-button';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import ImgCam from '@/assets/images/camera.svg';
import Button from '@mui/material/Button';
import SendLinkModal from '@/component/wallet/send-link-modal';
import TextField from '@mui/material/TextField';

import { useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import Joi from 'joi';
import Erc721Token from '@/component/send/Erc721Token';
import { useSendNftInfo, useSendNftInfoActions, SendNftState } from '@/store/zustand/store';
import { CutStringTo0x } from '@/lib/Shorten';
import { toast } from 'react-toastify';
import { InputAdornment } from '@mui/material';
import { qr_camera_call } from '@/component/linkMobile/qrCam';
import { useWeb3 } from '@/hooks/use-web3';
import { COIN_NAME_NFT } from '@/lib/common/name-space';
import { bioLocalStorage, localGetStorage } from '@/lib/storage';
// /**
//  * @description Util
//  * @returns
//  */
// const formatToKoreanCurrency = (_value: number) => {
//   const value = _value.toString();
//   const numberValue = parseInt(value.replace(/,/g, ''), 10); // 콤마를 제거하고 숫자로 변환합니다.
//   return isNaN(numberValue) ? '' : numberValue.toLocaleString('ko-KR'); // 숫자가 아니라면 빈 문자열을 반환하고, 숫자라면 한국의 숫자 형식으로 변환합니다.
// };

// TODO 전송 방식 send(interface) eth, erc20, erc721 각각 컴포넌트화

type FormDataType = {
  fromAddr: string;
  toAddr: string;
  contract: string;
  tokenId: string;
};

const schema = Joi.object({
  fromAddr: Joi.string().required().messages({
    'string.empty': '주소를 입력해 주세요.',
  }),
  toAddr: Joi.string().required().messages({
    'string.empty': '받는 주소를 입력해 주세요.',
  }),
  contract: Joi.string().required().messages({
    'string.empty': '컨트랙트 주소를 입력해 주세요.',
  }),
  tokenId: Joi.string().required().messages({
    'string.empty': '토큰아이디를 입력해 주세요.',
  }),
});

export default function SendNft() {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<FormDataType>({
    resolver: joiResolver(schema),
    mode: 'onChange',
  });

  const [openModal, setOpenModal] = useState(false);
  const sendNftInfo = useSendNftInfo();
  const { setSendNftInfo } = useSendNftInfoActions();
  const navigate = useNavigate();

  const { web3authCall } = useWeb3();

  useEffect(() => {
    if (sendNftInfo.contract && sendNftInfo.tokenId) {
      setValue('contract', sendNftInfo.contract);
      setValue('tokenId', sendNftInfo.tokenId);
    }
    return () => {};
  }, [setValue, sendNftInfo]);

  useEffect(() => {
    if (!web3authCall?.connected()) {
      return;
    }
    (async () => {
      const address = await web3authCall.getAddress();
      setValue('fromAddr', address);
    })();

    return () => {};
  }, [web3authCall]);

  window.appInterFace = (val, data) => {
    console.log(`in camCallback`);
    appInterFace(val);
  };
  const appInterFace = (val: any) => {
    // if (!val.startsWith('0x')) {
    //   toast.error('올바른 주소가 아닙니다.');
    //   return;
    // }
    setValue('toAddr', val);
  };

  const onSubmit = (data: FormDataType) => {
    const payload: SendNftState = {
      fromAddr: data.fromAddr,
      toAddr: data.toAddr,
      contract: data.contract,
      tokenId: data.tokenId,
      tokenType: sendNftInfo.tokenType,
    };
    setSendNftInfo(payload);

    // SNS 유저 + 생체인증 등록 여부 확인
    const snsAddress = localGetStorage('snsAddress');
    const bioCheck = bioLocalStorage();

    if (snsAddress && bioCheck) {
      // SNS 유저이고 생체인증 등록됨 -> 생체인증 화면으로
      navigate('/bioAuthComponent', {
        state: {
          title: 'NFT 전송 인증',
          returnUrl: '/wallet',
          options: {
            coin: COIN_NAME_NFT,
          },
        },
      });
    } else {
      // 그 외 -> PIN 입력 화면으로
      navigate('/pinComponent', {
        state: {
          pinType: 'confirm',
          title: 'NFT 전송 핀번호 확인',
          returnUrl: '/wallet',
          options: {
            coin: COIN_NAME_NFT,
          },
        },
      });
    }
  };

  return (
    <>
      <Container
        maxWidth="sm"
        sx={{
          minHeight: '812px',
          height: '100vh',
          position: 'relative',
        }}
      >
        <TopBackButton
          title="NFT 전송"
          boxProps={{
            mb: 4,
          }}
        />
        <form onSubmit={handleSubmit(onSubmit)}>
          <Typography variant="body1" mb={1}>
            <b>From</b>
          </Typography>
          <TextField
            fullWidth
            error={!!errors.fromAddr}
            helperText={errors.fromAddr?.message}
            {...register('fromAddr')}
            disabled
          />
          <Typography variant="body1" mb={1}>
            <b>TO</b>
          </Typography>
          <TextField
            fullWidth
            error={!!errors.toAddr}
            helperText={errors.toAddr?.message}
            {...register('toAddr')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    onClick={() => {
                      qr_camera_call();
                    }}
                  >
                    <ImgCam />
                  </Button>
                </InputAdornment>
              ),
            }}
          />
          <Typography variant="body1" mb={1}>
            <b>Contract Address</b>
          </Typography>
          <TextField
            fullWidth
            error={!!errors.contract}
            helperText={errors.contract?.message}
            {...register('contract')}
            disabled
          />
          <Typography variant="body1" mb={1}>
            <b>Token ID</b>
          </Typography>
          <TextField
            fullWidth
            error={!!errors.tokenId}
            helperText={errors.tokenId?.message}
            {...register('tokenId')}
            disabled
          />
          <Box position="absolute" bottom={0} left={0} width="100%">
            <Container maxWidth="sm">
              <Stack direction="row" gap={1} mb={3}>
                <Button variant="outlined" size="large" fullWidth type="submit">
                  다음
                </Button>
              </Stack>
            </Container>
          </Box>
        </form>
        <SendLinkModal
          open={openModal}
          onClose={() => {
            setOpenModal(false);
          }}
        />
      </Container>
    </>
  );
}
